import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';
import { normalizeShippingAddress, type ShippingAddress } from '../utils/checkoutShipping';

export enum ProductType {
  TUTORIAL = 'Tutorial',
  WORKSHOP = 'Workshop',
  ITEM = 'Item',
  FILE = 'File'
}

interface CheckoutItem {
  item: string;
  productType: ProductType;
  quantity: number;
  price: number;
  category?: string;
  isCustomizable?: boolean;
  productDetails: {
    title: string;
    description: string;
    image: string;
  };
}

interface PurchaseHistoryItem {
  productType: ProductType;
  item: any; // This will be populated with the actual item data
  amount: number;
  time: string;
  status: 'completed' | 'refunded' | 'cancelled';
  productDetails?: {
    title: string;
    description: string;
    image: string;
    downloadPath?: string; // For files
  };
}

export interface CheckoutSession {
  items: CheckoutItem[];
  total: number;
  _id: string;
  shippingAddress?: ShippingAddress;
}

interface CheckoutContextType {
  items: CheckoutItem[];
  sessionId: string;
  total: number;
  savedShippingAddress?: ShippingAddress;
  purchaseHistory: PurchaseHistoryItem[];
  isPurchaseHistoryLoading: boolean;
  addToCheckout: (itemId: string, productType: ProductType, quantity?: number) => Promise<void>;
  removeFromCheckout: (tutorialId: string) => Promise<void>;
  updateQuantity: (tutorialId: string, quantity: number) => Promise<void>;
  clearCheckout: () => Promise<void>;
  saveShippingAddress: (address: ShippingAddress) => Promise<void>;
  fetchCheckoutSession: () => Promise<void>;
  fetchPurchaseHistory: () => Promise<void>;
  refreshPurchaseHistory: () => Promise<void>;
}

const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

export const CheckoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [checkoutSession, setCheckoutSession] = useState<CheckoutSession>({ items: [], total: 0, _id: '' });
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistoryItem[]>([]);
  const [isPurchaseHistoryLoading, setIsPurchaseHistoryLoading] = useState(false);

  const fetchCheckoutSession = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    
    try {
      const response = await api.get<{ data: CheckoutSession & { totalPrice?: string } }>(
        `/checkout-sessions/user/${user.id}`,
        { params: { includePrice: true } }
      );
      const raw = response.data.data;
      const activeSession: CheckoutSession = raw
        ? {
            items: raw.items ?? [],
            total: Number(raw.totalPrice ?? raw.total ?? 0),
            _id: typeof raw._id === 'string' ? raw._id : String(raw._id ?? ''),
            shippingAddress: raw.shippingAddress,
          }
        : { items: [], total: 0, _id: '' };
      setCheckoutSession(activeSession);
    } catch (error) {
      console.error('Error fetching checkout session:', error);
    }
  }, [isAuthenticated, user?.id]);

  const fetchPurchaseHistory = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    
    try {
      setIsPurchaseHistoryLoading(true);
      const response = await api.get<{ data: PurchaseHistoryItem[] }>('/orders/user');
      setPurchaseHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching purchase history:', error);
    } finally {
      setIsPurchaseHistoryLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  const refreshPurchaseHistory = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      return;
    }
    
    try {
      const response = await api.get<{ data: PurchaseHistoryItem[] }>('/orders/user');
      setPurchaseHistory(response.data.data || []);
    } catch (error: any) {
      console.error('Error refreshing purchase history:', error);
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchCheckoutSession();
      // Note: Purchase history is now fetched on-demand by pages that need it
      // This prevents duplicate API calls
    }
  }, [isAuthenticated, user?.id]);

  const addToCheckout = useCallback(async (itemId: string, productType: ProductType, quantity: number = 1) => {
    try {
      let sessionId = checkoutSession._id;
      
      if (!sessionId) {
        const response = await api.post<{ data: { _id: string } }>('/checkout-sessions', { items: [], userId: user?.id });
        sessionId = response.data.data._id;
      }
      
      const existingItems = checkoutSession.items.map(({ item, quantity, productType }) => ({ item, quantity, productType }));
      let updatedItems;
      if (productType === ProductType.TUTORIAL || productType === ProductType.FILE || productType === ProductType.WORKSHOP) {
        const existingItem = existingItems.find(item => item.item === itemId);
        if (!existingItem) {
          updatedItems = [...existingItems, { item: itemId, quantity: 1, productType }];
        }
        else {
          updatedItems = existingItems;
        }
      }
      else {
        const productItem = existingItems.find(item => item.item === itemId);
        if (productItem) {
          productItem.quantity += quantity;
          updatedItems = existingItems;
        } else {
          updatedItems = [...existingItems, { item: itemId, quantity: quantity, productType }];
        }
      }
      
      await api.put(`/checkout-sessions/${sessionId}`, {
        items: updatedItems
      });
      
      await fetchCheckoutSession();
    } catch (error) {
      console.error('Error adding item to checkout:', error);
      throw error;
    }
  }, [checkoutSession._id, checkoutSession.items, user?.id, fetchCheckoutSession]);

  const removeFromCheckout = useCallback(async (itemId: string) => {
    try {
      await api.put(`/checkout-sessions/${checkoutSession._id}`, { 
        items: checkoutSession.items.filter(item => item.item !== itemId)
      });
      await fetchCheckoutSession();
    } catch (error) {
      console.error('Error removing item from checkout:', error);
      throw error;
    }
  }, [checkoutSession._id, checkoutSession.items, fetchCheckoutSession]);

  const updateQuantity = useCallback(async (itemId: string, quantity: number) => {
    try {
      const updatedItems = checkoutSession.items.map(item => 
        item.item === itemId ? { ...item, quantity } : item
      );
      
      await api.put(`/checkout-sessions/${checkoutSession._id}`, { 
        items: updatedItems
      });
      await fetchCheckoutSession();
    } catch (error) {
      console.error('Error updating item quantity:', error);
      throw error;
    }
  }, [checkoutSession._id, checkoutSession.items, fetchCheckoutSession]);

  const clearCheckout = useCallback(async () => {
    try {
      await api.put(`/checkout-sessions/${checkoutSession._id}`, {
        items: [],
        shippingAddress: null,
      });
      await fetchCheckoutSession();
    } catch (error) {
      console.error('Error clearing checkout:', error);
      throw error;
    }
  }, [checkoutSession._id, fetchCheckoutSession]);

  const saveShippingAddress = useCallback(
    async (address: ShippingAddress) => {
      const sessionId = checkoutSession._id;
      if (!sessionId) {
        console.error('Cannot save shipping address: checkout session id is missing');
        return;
      }

      const normalizedAddress = normalizeShippingAddress(address);

      const response = await api.put<{ data: CheckoutSession }>(
        `/checkout-sessions/${sessionId}`,
        { shippingAddress: normalizedAddress }
      );

      const saved = response.data.data?.shippingAddress ?? address;
      setCheckoutSession((prev) => ({
        ...prev,
        shippingAddress: saved,
      }));
    },
    [checkoutSession._id]
  );

  return (
    <CheckoutContext.Provider
      value={{
        items: checkoutSession.items,
        sessionId: checkoutSession._id,
        total: checkoutSession.total,
        savedShippingAddress: checkoutSession.shippingAddress,
        purchaseHistory,
        isPurchaseHistoryLoading,
        addToCheckout,
        removeFromCheckout,
        updateQuantity,
        clearCheckout,
        saveShippingAddress,
        fetchCheckoutSession,
        fetchPurchaseHistory,
        refreshPurchaseHistory,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error('useCheckout must be used within a CheckoutProvider');
  }
  return context;
}; 